import { useState, useEffect } from "react";
import axios from "axios";
import { API_ROUTES } from "../../../api/routes";
import { money } from "../utils/formatters";
import { toast } from "react-toastify";

const TIPO_ICONS = {
    diario: "📅",
    semanal: "📆",
    mensual: "🗓️",
    anual: "📊"
};

const TIPO_LABELS = {
    diario: "Diario",
    semanal: "Semanal",
    mensual: "Mensual",
    anual: "Anual"
};

// Modal de confirmación
function ModalConfirmacion({ cargo, onConfirm, onCancel }) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">⚠️</span>
                    <h3 className="text-lg font-bold text-gray-900">
                        Desactivar cargo recurrente
                    </h3>
                </div>

                {/* Contenido */}
                <div className="mb-6">
                    <p className="text-gray-700 mb-4">
                        ¿Estás seguro de desactivar este cargo recurrente?
                    </p>

                    {/* Info del cargo */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">{TIPO_ICONS[cargo.tipo_recurrencia]}</span>
                            <span className="font-medium text-purple-900">
                                {cargo.producto_nombre || cargo.descripcion}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-purple-600">Importe:</span>
                            <strong className="text-purple-800">{money(cargo.importe)}</strong>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-purple-600">Tipo:</span>
                            <span className="text-purple-800 capitalize">{TIPO_LABELS[cargo.tipo_recurrencia]}</span>
                        </div>
                        {cargo.tipo_ocurrencia === 'especifico' && (
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-600">Progreso:</span>
                                <span className="text-purple-800">
                                    {cargo.ejecuciones_realizadas} de {cargo.num_ocurrencias} ejecutadas
                                </span>
                            </div>
                        )}
                    </div>

                    <p className="text-sm text-gray-600 mt-4">
                        El cargo no se ejecutará más automáticamente. Podrás reactivarlo después si lo necesitas.
                    </p>
                </div>

                {/* Botones */}
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
                    >
                        Sí, desactivar
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function CargosRecurrentesCard({ empresaId }) {
    const [cargos, setCargos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandido, setExpandido] = useState(false);
    const [procesando, setProcesando] = useState(null);
    const [cargoADesactivar, setCargoADesactivar] = useState(null); // Para el modal

    const fetchCargos = async () => {
        if (!empresaId) return;

        try {
            setLoading(true);
            const response = await axios.get(
                API_ROUTES.cargosRecurrentes.listar(empresaId),
                { withCredentials: true }
            );

            // Filtrar solo activos
            const activos = response.data.filter(c => c.activo);
            setCargos(activos);
            setError(null);
        } catch (err) {
            console.error("Error cargando cargos recurrentes:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCargos();
    }, [empresaId]);

    // Abrir modal de confirmación
    const handleClickDesactivar = (cargo) => {
        setCargoADesactivar(cargo);
    };

    // Confirmar desactivación
    const handleConfirmarDesactivar = async () => {
        if (!cargoADesactivar) return;

        try {
            setProcesando(cargoADesactivar.id);
            await axios.patch(
                API_ROUTES.cargosRecurrentes.desactivar(cargoADesactivar.id, empresaId),
                {},
                { withCredentials: true }
            );

            toast.success("Cargo desactivado correctamente");
            setCargoADesactivar(null); // Cerrar modal
            await fetchCargos(); // Recargar lista
        } catch (err) {
            console.error("Error desactivando cargo:", err);
            toast.error(err.response?.data?.message || "Error al desactivar cargo");
        } finally {
            setProcesando(null);
        }
    };

    // Cancelar desactivación
    const handleCancelarDesactivar = () => {
        setCargoADesactivar(null);
    };

    // Calcular totales
    const totalMensual = cargos
        .filter(c => c.tipo_recurrencia === "mensual")
        .reduce((sum, c) => sum + Number(c.importe), 0);

    const proximoCargo = cargos.length > 0
        ? cargos.reduce((min, c) =>
            new Date(c.fecha_proxima_ejecucion) < new Date(min.fecha_proxima_ejecucion) ? c : min
        )
        : null;

    if (loading) {
        return (
            <div className="rounded-md border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-2 hover:shadow-sm">
                <div className="flex items-center gap-2">
                    <span className="text-base">🔄</span>
                    <p className="text-[10px] font-bold uppercase text-purple-700 tracking-wider">
                        CARGOS RECURRENTES
                    </p>
                </div>
                <div className="text-xs text-purple-600 mt-2">Cargando...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-md border-2 border-red-200 bg-gradient-to-br from-red-50 to-pink-50 p-2">
                <div className="flex items-center gap-2">
                    <span className="text-base">⚠️</span>
                    <p className="text-[10px] font-bold uppercase text-red-700 tracking-wider">
                        ERROR
                    </p>
                </div>
                <div className="text-xs text-red-600 mt-2">Error al cargar cargos</div>
            </div>
        );
    }

    if (cargos.length === 0) {
        return (
            <div className="rounded-md border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50 p-2 hover:shadow-sm">
                <div className="flex items-center gap-2">
                    <span className="text-base">💤</span>
                    <p className="text-[10px] font-bold uppercase text-gray-700 tracking-wider">
                        CARGOS RECURRENTES
                    </p>
                </div>
                <div className="text-xs text-gray-600 mt-2">Sin cargos activos</div>
            </div>
        );
    }

    return (
        <>
            <div className="rounded-md border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-2 hover:shadow-sm">
                {/* Header */}
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                        <span className="text-base">🔁</span>
                        <p className="text-[10px] font-bold uppercase text-purple-700 tracking-wider">
                            CARGOS RECURRENTES
                        </p>
                        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-purple-200 text-purple-800 font-medium">
                            {cargos.length}
                        </span>
                    </div>
                    <button
                        onClick={() => setExpandido(!expandido)}
                        className="text-purple-600 hover:text-purple-800 text-xs font-medium"
                    >
                        {expandido ? "▼" : "▶"}
                    </button>
                </div>

                {/* Resumen compacto */}
                <div className="space-y-1 text-xs">
                    {/* Total mensual */}
                    <div className="flex justify-between items-center">
                        <span className="text-purple-600">🗓️ Total mensual</span>
                        <strong className="text-purple-800">{money(totalMensual)}</strong>
                    </div>

                    {/* Próximo cargo */}
                    {proximoCargo && (
                        <div className="bg-purple-100/50 px-2 py-1 rounded">
                            <div className="flex justify-between items-center">
                                <span className="text-purple-600 text-[11px]">⏰ Próximo cargo</span>
                                <span className="text-purple-700 text-[11px] font-medium">
                                    {new Date(proximoCargo.fecha_proxima_ejecucion).toLocaleDateString('es-MX', {
                                        day: '2-digit',
                                        month: 'short'
                                    })}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mt-0.5">
                                <span className="text-purple-600 text-[10px] truncate max-w-[120px]">
                                    {proximoCargo.producto_nombre || proximoCargo.descripcion}
                                </span>
                                <strong className="text-purple-800 text-[11px]">
                                    {money(proximoCargo.importe)}
                                </strong>
                            </div>
                        </div>
                    )}
                </div>

                {/* Lista expandida */}
                {expandido && (
                    <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto">
                        {cargos.map((cargo) => (
                            <div
                                key={cargo.id}
                                className="bg-white/70 border border-purple-200 rounded px-2 py-1.5 text-xs hover:bg-white transition-colors"
                            >
                                {/* Producto y tipo */}
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span>{TIPO_ICONS[cargo.tipo_recurrencia]}</span>
                                            <span className="font-medium text-purple-900 truncate">
                                                {cargo.producto_nombre || cargo.descripcion}
                                            </span>
                                        </div>
                                        <div className="text-[10px] text-purple-600 mt-0.5">
                                            {TIPO_LABELS[cargo.tipo_recurrencia]}
                                            {cargo.tipo_ocurrencia === 'especifico' && (
                                                <span className="ml-1">
                                                    ({cargo.ejecuciones_realizadas}/{cargo.num_ocurrencias})
                                                </span>
                                            )}
                                            {cargo.tipo_ocurrencia === 'infinito' && (
                                                <span className="ml-1 text-purple-500">∞</span>
                                            )}
                                        </div>
                                    </div>
                                    <strong className="text-purple-800 whitespace-nowrap">
                                        {money(cargo.importe)}
                                    </strong>
                                </div>

                                {/* Fechas */}
                                <div className="flex items-center justify-between text-[10px] text-purple-600 border-t border-purple-100 pt-1 mb-1">
                                    <div className="flex items-center gap-3">
                                        {cargo.fecha_ultima_ejecucion && (
                                            <span title="Última ejecución">
                                                ✓ {new Date(cargo.fecha_ultima_ejecucion).toLocaleDateString('es-MX', {
                                                    day: '2-digit',
                                                    month: 'short'
                                                })}
                                            </span>
                                        )}
                                        <span title="Próxima ejecución" className="text-purple-700 font-medium">
                                            → {new Date(cargo.fecha_proxima_ejecucion).toLocaleDateString('es-MX', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })}
                                        </span>
                                    </div>
                                </div>

                                {/* Botón de desactivar */}
                                <div className="flex justify-end pt-1 border-t border-purple-100">
                                    <button
                                        onClick={() => handleClickDesactivar(cargo)}
                                        disabled={procesando === cargo.id}
                                        className="text-[10px] px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                    >
                                        {procesando === cargo.id ? (
                                            <>
                                                <span className="animate-spin">⏳</span>
                                                Desactivando...
                                            </>
                                        ) : (
                                            <>
                                                🗑️ Desactivar
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Nota informativa */}
                {!expandido && cargos.length > 1 && (
                    <div className="text-[10px] text-purple-500 italic mt-1">
                        Click ▶ para ver {cargos.length} cargos activos
                    </div>
                )}
            </div>

            {/* Modal de confirmación */}
            {cargoADesactivar && (
                <ModalConfirmacion
                    cargo={cargoADesactivar}
                    onConfirm={handleConfirmarDesactivar}
                    onCancel={handleCancelarDesactivar}
                />
            )}
        </>
    );
}